/**
 * SayItBetter — In-App Purchase Billing Helper
 *
 * Wraps RevenueCat's Capacitor plugin for Google Play Billing + Apple StoreKit.
 * Works as a no-op in web preview (returns early when not on a native platform or
 * when RevenueCat API keys aren't configured) so the rest of the app stays
 * functional without real billing credentials.
 *
 * Usage (at launch):
 *   1. Set REACT_APP_REVENUECAT_ANDROID_KEY and REACT_APP_REVENUECAT_IOS_KEY in .env
 *   2. Set REACT_APP_LAUNCH_MODE=launched
 *   3. Build native Android/iOS via Capacitor (npx cap add android/ios + sync)
 *   4. Create the matching subscription products in Play Console + App Store Connect
 *
 * Product IDs expected: sayitbetter_pro_yearly, sayitbetter_pro_monthly
 */

import { Events } from "./analytics";

const ENTITLEMENT_PRO = "pro";
const PRODUCTS = {
  yearly: "sayitbetter_pro_yearly",
  monthly: "sayitbetter_pro_monthly",
};

let _initialized = false;
let _isNative = false;
let _enabled = false;
let _Purchases = null;

async function detectNative() {
  try {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function getApiKey() {
  const isIOS =
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
  return isIOS
    ? process.env.REACT_APP_REVENUECAT_IOS_KEY
    : process.env.REACT_APP_REVENUECAT_ANDROID_KEY;
}

/**
 * Initialize RevenueCat. Safe to call multiple times.
 * Returns true if billing is active, false if running as web/no-op.
 */
export async function initBilling(userId) {
  if (_initialized) return _enabled;
  _initialized = true;

  _isNative = await detectNative();
  if (!_isNative) {
    // Web preview — no billing available
    return false;
  }

  const apiKey = getApiKey();
  if (!apiKey) {
    if (process.env.NODE_ENV === "development") {
      console.info("[billing] RevenueCat API key not configured — billing disabled.");
    }
    return false;
  }

  try {
    const { Purchases, LOG_LEVEL } = await import("@revenuecat/purchases-capacitor");
    _Purchases = Purchases;
    await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
    await Purchases.configure({
      apiKey,
      appUserID: userId || undefined,
    });
    _enabled = true;
    return true;
  } catch (e) {
    console.warn("[billing] RevenueCat init failed:", e);
    return false;
  }
}

/**
 * Fetch current PRO entitlement status.
 * @returns {Promise<{active: boolean, expires_at?: string, product_id?: string}>}
 */
export async function getProStatus() {
  if (!_enabled || !_Purchases) return { active: false };
  try {
    const { customerInfo } = await _Purchases.getCustomerInfo();
    const ent = customerInfo?.entitlements?.active?.[ENTITLEMENT_PRO];
    if (!ent) return { active: false };
    return {
      active: true,
      expires_at: ent.expirationDate,
      product_id: ent.productIdentifier,
    };
  } catch (e) {
    return { active: false };
  }
}

/**
 * Purchase a subscription. Opens the native Play Billing / StoreKit modal.
 * @param {"yearly" | "monthly"} plan
 * @returns {Promise<{ok: boolean, error?: string, status?: object}>}
 */
export async function purchase(plan) {
  const productId = PRODUCTS[plan];
  if (!productId) return { ok: false, error: "Unknown plan" };

  if (!_enabled || !_Purchases) {
    return {
      ok: false,
      error: "In-app billing isn't available yet. Install the app from the store to subscribe.",
    };
  }

  try {
    // Get current offerings from the store
    const { current } = await _Purchases.getOfferings();
    const pkg = current?.availablePackages?.find(
      (p) => p.product?.identifier === productId
    );
    if (!pkg) {
      return { ok: false, error: `Product ${productId} not found in current offering.` };
    }
    const { customerInfo } = await _Purchases.purchasePackage({ aPackage: pkg });
    const active = !!customerInfo?.entitlements?.active?.[ENTITLEMENT_PRO];
    Events.trackEvent?.("purchase_success", { plan, product_id: productId });
    return {
      ok: active,
      status: {
        active,
        expires_at:
          customerInfo?.entitlements?.active?.[ENTITLEMENT_PRO]?.expirationDate,
      },
    };
  } catch (e) {
    if (e?.userCancelled) {
      return { ok: false, error: "Cancelled" };
    }
    return { ok: false, error: e?.message || "Purchase failed" };
  }
}

/**
 * Restore purchases (Apple requires this button in the app).
 */
export async function restorePurchases() {
  if (!_enabled || !_Purchases) {
    return { ok: false, error: "In-app billing not available." };
  }
  try {
    const { customerInfo } = await _Purchases.restorePurchases();
    const active = !!customerInfo?.entitlements?.active?.[ENTITLEMENT_PRO];
    return { ok: true, active };
  } catch (e) {
    return { ok: false, error: e?.message || "Restore failed" };
  }
}

export async function setBillingUser(userId) {
  if (!_enabled || !_Purchases) return;
  try {
    if (userId) {
      await _Purchases.logIn({ appUserID: userId });
    } else {
      await _Purchases.logOut();
    }
  } catch {
    // noop
  }
}

export const BILLING = {
  products: PRODUCTS,
  entitlement: ENTITLEMENT_PRO,
};
